import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  Compass, HandHeart, Users, Calendar, HeartHandshake, ArrowRight,
  Sparkle, ShieldCheck, Leaf, BookOpen,
} from "lucide-react";

const steps = [
  { n: "01", title: "Discover", text: "Sense what you're seeking. There's no wrong door." },
  { n: "02", title: "Gauge", text: "Reflect on where you are with a gentle self-exploration." },
  { n: "03", title: "Join", text: "Become a Discovery Member to save your journey." },
  { n: "04", title: "Participate", text: "Book an offering, join a circle, or steward your community." },
  { n: "05", title: "Practice", text: "Return daily with your GAUGE guide and chosen practices." },
  { n: "06", title: "Enhance Your World", text: "Well-being becomes real in how you walk with others." },
];

const doors = [
  { title: "Start with GAUGE Exploration", text: "A private, self-guided reflection across your whole life.", to: "/gauge/app", icon: Compass, accent: "#144F75", cta: "Begin Exploration" },
  { title: "Book a Wellness Offering", text: "Float, reiki, breathwork, bodywork, coaching, and more.", to: "/wellness", icon: HandHeart, accent: "#D98E73", cta: "Browse Offerings" },
  { title: "Join as a Discovery Member", text: "Enter the community container and save your journey.", to: "/join", icon: Sparkle, accent: "#5B8A72", cta: "Join Discovery" },
  { title: "Explore Community", text: "Circles, groups, reflections, and conscious participation.", to: "/community", icon: Users, accent: "#144F75", cta: "See Community" },
  { title: "View Events & Retreats", text: "Spirals, retreats, workshops, and gatherings.", to: "/events", icon: Calendar, accent: "#8FA89B", cta: "See What's Coming" },
  { title: "Create or Join Stewardship", text: "Support people, places, land, and future generations.", to: "/stewardship", icon: HeartHandshake, accent: "#D98E73", cta: "Find Opportunities" },
];

export default function StartHere() {
  return (
    <div data-testid="start-here-page" className="bg-[#F9F7F3]">
      <Seo title="Start Here" description="New to Enhancing Your World? Find your first step — book an offering, meet an enhancer, join an event, explore membership or begin the GAUGE daily practice." />
      {/* Hero */}
      <section className="bg-[#144F75] py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D98E73] mb-4">Start Here</p>
          <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tighter leading-none">What are you seeking?</h1>
          <p className="text-white/85 text-lg md:text-xl mt-6 max-w-2xl mx-auto leading-relaxed">
            You don't need to understand the whole ecosystem to begin. Choose one door.
            The pathway reveals itself as you walk it.
          </p>
        </div>
      </section>

      {/* The pathway */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="start-pathway">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8FA89B] mb-3">The Living Pathway</p>
        <h2 className="font-serif text-3xl md:text-4xl text-[#1A242B] tracking-tight mb-10 max-w-xl">One gentle movement, from where you are toward how you want to live.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {steps.map((s, i) => (
            <div key={s.n} className="bg-white rounded-2xl border border-[#144F75]/10 p-6 shadow-sm eyw-reveal" style={{ animationDelay: `${i * 60}ms` }}>
              <span className="font-serif text-3xl text-[#D98E73]">{s.n}</span>
              <h3 className="font-serif text-xl text-[#1A242B] mt-2">{s.title}</h3>
              <p className="text-[#4A5D6B] text-sm mt-1.5">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Doors */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-8" data-testid="start-doors">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {doors.map((d, i) => {
            const Icon = d.icon;
            return (
              <Link key={d.title} to={d.to} data-testid={`start-door-${i}`}
                className="group bg-white rounded-2xl border border-[#144F75]/10 p-7 shadow-sm transition-transform hover:-translate-y-1 hover:shadow-md flex flex-col">
                <span className="grid place-items-center h-12 w-12 rounded-xl mb-5" style={{ background: `${d.accent}14`, color: d.accent }}><Icon size={22} /></span>
                <h3 className="font-serif text-2xl text-[#1A242B] leading-snug">{d.title}</h3>
                <p className="text-[#4A5D6B] text-sm mt-2 flex-1">{d.text}</p>
                <span className="inline-flex items-center gap-1 text-[#144F75] text-sm font-medium mt-5 group-hover:gap-2 transition-all">{d.cta} <ArrowRight size={15} /></span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Discovery Membership explainer */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="start-discovery">
        <div className="rounded-3xl bg-white border border-[#144F75]/10 shadow-sm p-9 md:p-12 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#8FA89B] mb-3">The Welcoming Doorway</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A242B] tracking-tight">Discovery Membership</h2>
            <p className="text-[#4A5D6B] text-lg mt-4 leading-relaxed">
              Discovery Membership is not just a free account — it's your entrance into the
              community container. You review and sign the participation agreement, save your
              GAUGE Exploration, join selected opportunities, and begin walking with the community.
            </p>
            <ul className="mt-6 space-y-2.5">
              {[
                { icon: ShieldCheck, t: "Sign the participation agreement (PMA)" },
                { icon: Compass, t: "Save & track your GAUGE journey" },
                { icon: Leaf, t: "Join stewardship & community opportunities" },
                { icon: BookOpen, t: "Receive recommended next steps" },
              ].map((f) => {
                const Icon = f.icon;
                return (
                  <li key={f.t} className="flex items-center gap-3 text-[#1A242B]">
                    <Icon size={17} className="text-[#5B8A72] shrink-0" /> <span className="text-[15px]">{f.t}</span>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/join" data-testid="start-join-btn" className="eyw-btn">Join as a Discovery Member <ArrowRight size={16} /></Link>
              <Link to="/memberships" className="eyw-btn-outline">Compare Memberships</Link>
            </div>
          </div>
          <div className="rounded-2xl bg-[#144F75] p-8 text-center">
            <Sparkle className="mx-auto text-[#D98E73] mb-4" size={30} />
            <p className="font-serif text-2xl text-white leading-snug">"Start where you are. Enhance your world through the way you walk."</p>
          </div>
        </div>
      </section>
    </div>
  );
}
